import { StateCreator } from 'zustand';

import type { DirectoryRow } from '../../services/api';
import type { PendingChip } from '../../hooks/chat/useAttachmentUpload';
import type { MentionPick } from '../../components/chat/mentions';
import {
  closeWindow,
  initialChatWindows,
  minimizeWindow,
  NEW_MESSAGE_WINDOW,
  openWindow,
  replaceWindow,
  setMaxOpen,
  toggleExpanded,
  type ChatWindowsState,
} from './chatWindows';

export type ChatLayout = 'tray' | 'sheet';
export type WindowView = 'conversation' | 'members' | 'announcers';

export interface ChatSlice {
  chatWindows: ChatWindowsState;
  chatLayout: ChatLayout;
  chatSheetOpen: boolean;
  /** The phone sheet shows `chatWindows.open[0]` or the inbox. */
  chatSheetView: 'inbox' | 'conversation';
  windowViews: Record<string, WindowView>;
  /**
   * EMR-2272: the episode whose chart is on screen, published by
   * `useSetChatContextEpisode` so the inbox can pin that episode's care-team
   * thread. Session state, like the other chat fields: never persisted.
   */
  chatContextEpisodeId: string | null;
  setChatContextEpisodeId: (id: string | null) => void;
  /** New message recipients; the text is `chatDrafts[NEW_MESSAGE_WINDOW]`. */
  newMessageRecipients: DirectoryRow[];
  /** Changes when a compose ends (close or resolve); a minimize keeps it. */
  newMessageComposeId: number;
  setNewMessageRecipients: (rows: DirectoryRow[]) => void;
  chatDrafts: Record<string, string>;
  setChatDraft: (channelId: string, draft: string) => void;
  /** EMR-2560: the members picked from the @ list, per channel. Not persisted. */
  chatDraftMentions: Record<string, MentionPick[]>;
  setChatDraftMentions: (channelId: string, picks: MentionPick[]) => void;
  /** EMR-1878: pending attachment chips per channel, like `chatDrafts`. */
  chatChips: Record<string, PendingChip[]>;
  setChips: (
    channelId: string,
    next: (current: PendingChip[]) => PendingChip[]
  ) => void;
  /** Opens (or restores) a window; the sheet layout also opens the sheet. */
  openChatWindow: (id: string) => void;
  minimizeChatWindow: (id: string) => void;
  /** Drafts stay: closing a window does not discard what was typed. */
  closeChatWindow: (id: string) => void;
  toggleChatWindowExpanded: (id: string) => void;
  replaceChatWindow: (fromId: string, toId: string) => void;
  setChatWindowView: (id: string, view: WindowView) => void;
  setChatDockOpen: (open: boolean) => void;
  setChatMaxOpen: (n: 1 | 2) => void;
  setChatLayout: (layout: ChatLayout) => void;
  setChatSheetOpen: (open: boolean) => void;
  setChatSheetView: (view: 'inbox' | 'conversation') => void;
  openChatInbox: () => void;
  /**
   * EMR-2534: announcers also list announcement channels of clinics they are
   * not in. The one chat field that is persisted (per browser), and it is not
   * in `chatSessionReset`.
   */
  chatShowAllAnnouncements: boolean;
  setChatShowAllAnnouncements: (on: boolean) => void;
}

/** Members and Announcers replace the messages, so nothing is read there. */
const isConversationView = (state: ChatSlice, id: string) =>
  (state.windowViews[id] ?? 'conversation') === 'conversation';

/**
 * The conversation ids a person can see right now, which is what mark-read and
 * the live catch-up gate on. Minimized windows and the New message window are
 * not conversations on screen.
 */
export function selectVisibleChannelIds(state: ChatSlice): string[] {
  if (state.chatLayout === 'sheet') {
    const first = state.chatWindows.open[0];
    return state.chatSheetOpen &&
      state.chatSheetView === 'conversation' &&
      first &&
      first !== NEW_MESSAGE_WINDOW &&
      isConversationView(state, first)
      ? [first]
      : [];
  }
  return state.chatWindows.open.filter(
    id => id !== NEW_MESSAGE_WINDOW && isConversationView(state, id)
  );
}

/** Whether any chat surface (dock, window or sheet) is on screen. */
export function selectChatSurfaceOpen(state: ChatSlice): boolean {
  return state.chatLayout === 'sheet'
    ? state.chatSheetOpen
    : state.chatWindows.dockOpen || state.chatWindows.open.length > 0;
}

/** Closing New message, or resolving it to a conversation, ends that compose. */
function clearedNewMessage(state: ChatSlice) {
  const chatDrafts = { ...state.chatDrafts };
  delete chatDrafts[NEW_MESSAGE_WINDOW];
  return {
    chatDrafts,
    newMessageRecipients: [],
    newMessageComposeId: state.newMessageComposeId + 1,
  };
}

/** What logout puts back: nothing of the last person's chat session stays. */
export const chatSessionReset = () => ({
  chatWindows: initialChatWindows,
  chatSheetOpen: false,
  chatSheetView: 'inbox' as const,
  windowViews: {},
  chatDrafts: {},
  chatDraftMentions: {},
  chatChips: {},
  newMessageRecipients: [],
  newMessageComposeId: 0,
});

/**
 * Chat window state. Only `chatShowAllAnnouncements` is in the store's
 * `partialize`: drafts are per session by ruling, and a persisted open window
 * would reopen on every load.
 */
export const chatSlice: StateCreator<ChatSlice> = set => ({
  chatWindows: initialChatWindows,
  chatLayout: 'tray',
  chatSheetOpen: false,
  chatSheetView: 'inbox',
  windowViews: {},
  chatContextEpisodeId: null,
  chatDrafts: {},
  chatDraftMentions: {},
  chatChips: {},
  newMessageRecipients: [],
  newMessageComposeId: 0,
  chatShowAllAnnouncements: false,
  setChatShowAllAnnouncements: on => set({ chatShowAllAnnouncements: on }),
  setNewMessageRecipients: rows => set({ newMessageRecipients: rows }),

  setChatContextEpisodeId: id => set({ chatContextEpisodeId: id }),
  setChatDraft: (channelId, draft) =>
    set(state => ({ chatDrafts: { ...state.chatDrafts, [channelId]: draft } })),
  setChatDraftMentions: (channelId, picks) =>
    set(state => ({
      chatDraftMentions: { ...state.chatDraftMentions, [channelId]: picks },
    })),
  setChips: (channelId, next) =>
    set(state => ({
      chatChips: {
        ...state.chatChips,
        [channelId]: next(state.chatChips[channelId] ?? []),
      },
    })),

  openChatWindow: id =>
    set(state => ({
      chatWindows: openWindow(state.chatWindows, id),
      windowViews: { ...state.windowViews, [id]: 'conversation' },
      ...(state.chatLayout === 'sheet'
        ? { chatSheetOpen: true, chatSheetView: 'conversation' as const }
        : {}),
    })),
  minimizeChatWindow: id =>
    set(state => ({ chatWindows: minimizeWindow(state.chatWindows, id) })),
  closeChatWindow: id =>
    set(state => {
      const windowViews = { ...state.windowViews };
      delete windowViews[id];
      return {
        chatWindows: closeWindow(state.chatWindows, id),
        windowViews,
        ...(id === NEW_MESSAGE_WINDOW ? clearedNewMessage(state) : {}),
      };
    }),
  toggleChatWindowExpanded: id =>
    set(state => ({ chatWindows: toggleExpanded(state.chatWindows, id) })),
  replaceChatWindow: (fromId, toId) =>
    set(state => {
      const windowViews = { ...state.windowViews };
      delete windowViews[fromId];
      return {
        chatWindows: replaceWindow(state.chatWindows, fromId, toId),
        windowViews: { ...windowViews, [toId]: 'conversation' },
        ...(fromId === NEW_MESSAGE_WINDOW ? clearedNewMessage(state) : {}),
      };
    }),
  setChatWindowView: (id, view) =>
    set(state => ({ windowViews: { ...state.windowViews, [id]: view } })),
  setChatDockOpen: open =>
    set(state => ({ chatWindows: { ...state.chatWindows, dockOpen: open } })),
  // A resize that lands on the same value must not re-render the tray.
  setChatMaxOpen: n =>
    set(state =>
      n === state.chatWindows.maxOpen
        ? state
        : { chatWindows: setMaxOpen(state.chatWindows, n) }
    ),
  setChatLayout: layout =>
    set(state =>
      state.chatLayout === layout ? state : { chatLayout: layout }
    ),
  setChatSheetOpen: open => set({ chatSheetOpen: open }),
  setChatSheetView: view => set({ chatSheetView: view }),
  openChatInbox: () =>
    set(state =>
      state.chatLayout === 'sheet'
        ? { chatSheetOpen: true, chatSheetView: 'inbox' as const }
        : { chatWindows: { ...state.chatWindows, dockOpen: true } }
    ),
});
